'use client';

import clsx from 'clsx';
import {
  type ForwardedRef,
  type MutableRefObject,
  type UIEventHandler,
  useContext,
  useImperativeHandle,
  useRef,
} from 'react';
import { usePreventScroll } from 'react-aria';
import {
  Button as RACButton,
  Collection as RACCollection,
  ComboBox as RACComboBox,
  ComboBoxStateContext as RACComboBoxStateContext,
  Group as RACGroup,
  Header as RACHeader,
  Input as RACInput,
  ListBox as RACListBox,
  ListBoxLoadMoreItem as RACListBoxLoadMoreItem,
  ListBoxItem as RACListBoxItem,
  ListBoxSection as RACListBoxSection,
  ListLayout as RACListLayout,
  type ListLayoutOptions as RACListLayoutOptions,
  Text as RACText,
  type ComboBoxProps as RACComboBoxProps,
  type ListBoxItemProps as RACListBoxItemProps,
  Virtualizer as RACVirtualizer,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { IconCheckBold, IconChevronDown } from '../../icons/index.js';
import { isProduction } from '../../utils/is-production.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { CollapsibleList } from '../internal/collapsible-list/collapsible-list.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import { XButton } from '../internal/x-button/x-button.js';
import { Spinner } from '../spinner/spinner.js';
import type {
  ApiProps,
  AsyncItemLoadingProps,
  CollectionItem,
  CollectionProps,
  CommonProps,
  FieldWithPlaceholderProps,
  Key,
  StringLikeChildren,
} from '../types.js';
import { textStyle } from '../typography/utils.js';

const VIRTUAL_LAYOUT_OPTIONS: RACListLayoutOptions = {
  estimatedRowHeight: 40,
  estimatedHeadingHeight: 32,
  loaderHeight: 40,
  padding: 0,
  gap: 0,
};

/** Represents the state of the `ComboBox` component for validation purposes. */
export interface ComboBoxValidationValue {
  /** The selected key in the combo box. */
  value: Key | null;
  /** The value of the combo box input. */
  inputValue: string;
  /**
   * The selected key in the combo box.
   * @deprecated Use `value` instead.
   */
  selectedKey: Key | null;
}

export interface ComboBoxProps<T extends CollectionItem = CollectionItem>
  extends
    CommonProps,
    FieldWithPlaceholderProps<ComboBoxValidationValue>,
    CollectionProps<T>,
    AsyncItemLoadingProps,
    ApiProps<ComboBoxApi>,
    Pick<
      RACComboBoxProps<T>,
      | 'isDisabled'
      | 'isRequired'
      | 'isInvalid'
      | 'isReadOnly'
      | 'inputValue'
      | 'defaultInputValue'
      | 'autoFocus'
      | 'onInputChange'
      | 'onFocus'
      | 'onBlur'
      | 'onOpenChange'
    > {
  /** The current value (controlled). */
  value?: Key | null;
  /** The initial value (uncontrolled). */
  defaultValue?: Key;
  /** Handler that is called when the value changes. */
  onChange?: (key: Key | null) => void;
  /**
   * The currently selected key in the collection (controlled).
   * @deprecated Use `value` instead.
   */
  selectedKey?: Key | null;
  /**
   * The initial selected key in the collection (uncontrolled).
   * @deprecated Use `defaultValue` instead.
   */
  defaultSelectedKey?: Key;
  /**
   * Handler that is called when the selection changes.
   * @deprecated Use `onChange` instead.
   */
  onSelectionChange?: (key: Key | null) => void;
  /** Whether to display a clear button to clear the selection. */
  isClearable?: boolean;
  /**
   * Whether the list of options should be virtualized. Use this as a performance optimization for large lists.
   * @default false
   */
  isVirtualized?: boolean;
  /** Handler that is called when the list of items is scrolled. */
  onScroll?: UIEventHandler<HTMLDivElement>;
}

/** The imperative API exposed by the `ComboBox` component. */
export interface ComboBoxApi {
  /** Focuses the input field and opens the list of items. */
  focus: () => void;
  /** Selects the contents of the input field. */
  select: () => void;
}

function ComboBox<T extends CollectionItem>(
  {
    label,
    description,
    error,
    items,
    children,
    placeholder,
    UNSAFE_className,
    UNSAFE_style,
    isReadOnly,
    onScroll,
    isVirtualized = false,
    isClearable = false,
    UNSTABLE_isLoading: isLoading,
    UNSTABLE_onLoadMore: onLoadMore,
    apiRef,
    value,
    defaultValue,
    onChange,
    selectedKey,
    defaultSelectedKey,
    onSelectionChange,
    validate,
    ...props
  }: ComboBoxProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const inputRef = useRef<HTMLInputElement>(null);

  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('ComboBox requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  if (!isProduction() && items != null && typeof children !== 'function') {
    console.warn('`ComboBox` requires `children` to be a function when using `items` prop');
  }

  useImperativeHandle(
    apiRef,
    () => ({
      focus: () => inputRef.current?.focus(),
      select: () => inputRef.current?.select(),
    }),
    [],
  );

  const isFieldClearable = isClearable && !props.isDisabled && !isReadOnly;

  const listBox = (
    <ComboBoxList<T> items={items} isLoading={isLoading} onLoadMore={onLoadMore} onScroll={onScroll}>
      {children}
    </ComboBoxList>
  );

  return (
    <RACComboBox
      {...props}
      ref={ref}
      className={clsx('cim-combo-box', UNSAFE_className)}
      style={UNSAFE_style}
      selectionMode="single"
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      selectedKey={selectedKey}
      defaultSelectedKey={defaultSelectedKey}
      onSelectionChange={onSelectionChange}
      menuTrigger="focus"
      isReadOnly={isReadOnly}
      data-readonly={isReadOnly ? true : undefined}
      validate={
        validate
          ? ({ inputValue, value }) => {
              return validate({ inputValue, value, selectedKey: value });
            }
          : undefined
      }
    >
      <FormFieldLabel isRequired={props.isRequired} isDisabled={props.isDisabled}>
        {label}
      </FormFieldLabel>
      <RACGroup className="cim-combo-box-input-group">
        <ComboBoxInput
          inputRef={inputRef}
          isReadOnly={isReadOnly}
          placeholder={placeholder}
          isClearable={isFieldClearable}
        />
        {isFieldClearable && <ComboBoxClearButton onClear={() => inputRef.current?.focus()} />}
        <RACButton className="cim-combo-box-button">
          <IconChevronDown />
        </RACButton>
      </RACGroup>
      <FormFieldError>{error}</FormFieldError>
      <FormFieldDescription>{description}</FormFieldDescription>
      <CollapsibleList
        offset={5} // 1px border + 4px actual offset
        maxHeight={315}
      >
        {isVirtualized ? (
          <RACVirtualizer layout={RACListLayout} layoutOptions={VIRTUAL_LAYOUT_OPTIONS}>
            {listBox}
          </RACVirtualizer>
        ) : (
          listBox
        )}
      </CollapsibleList>
    </RACComboBox>
  );
}

/**
 * Allows users to filter a collapsible list and select one item from it.
 *
 * See [combo box usage guidelines](https://ui.cimpress.io/components/combo-box)
 */
const _ComboBox = withStyleProps(forwardRef(ComboBox), 'ComboBox');

export { _ComboBox as ComboBox };

function ComboBoxClearButton({ onClear }: { onClear?: () => void }) {
  const comboBoxState = useContext(RACComboBoxStateContext);
  const selectMessages = useLocalizedMessages('select');
  const inputValue = comboBoxState?.inputValue;

  if (!comboBoxState || inputValue === '') {
    return null;
  }

  return (
    <XButton
      onPress={() => {
        comboBoxState.setValue(null);
        onClear?.();
      }}
      iconType="circled"
      aria-label={selectMessages.format('clearSelection')}
      // Prevent this button from acting as a trigger for opening/closing the ComboBox
      slot={null}
      excludeFromTabOrder
    />
  );
}

function ComboBoxInput({
  inputRef,
  isReadOnly,
  placeholder,
  isClearable,
}: {
  inputRef: MutableRefObject<HTMLInputElement | null>;
  isReadOnly: boolean | undefined;
  placeholder: string | undefined;
  isClearable: boolean;
}) {
  const comboBoxState = useContext(RACComboBoxStateContext);

  return (
    <RACInput
      ref={inputRef}
      className={clsx('cim-combo-box-input', textStyle({ variant: 'body' }))}
      // Ensure the popover opens when the input is clicked.
      // By default, React Aria only opens the popover when the input is focused.
      onClick={() => !isReadOnly && comboBoxState!.open()}
      placeholder={placeholder}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !comboBoxState?.isOpen && isClearable) {
          comboBoxState?.setValue(null);
        }
      }}
    />
  );
}

function ComboBoxList<T extends CollectionItem = CollectionItem>({
  items,
  children,
  isLoading,
  onLoadMore,
  onScroll,
}: Pick<ComboBoxProps<T>, 'items' | 'children' | 'onScroll'> & {
  isLoading: ComboBoxProps['UNSTABLE_isLoading'];
  onLoadMore: ComboBoxProps['UNSTABLE_onLoadMore'];
}) {
  const comboBoxState = useContext(RACComboBoxStateContext);
  const collectionMessages = useLocalizedMessages('UNSTABLE_collection');

  usePreventScroll({ isDisabled: !comboBoxState?.isOpen });

  return (
    <RACListBox
      className="cim-combo-box-listbox"
      onScroll={onScroll}
      renderEmptyState={() =>
        isLoading ? <Spinner aria-label={collectionMessages.format('loading')} size="small" marginY={8} /> : null
      }
    >
      <RACCollection items={items}>{children}</RACCollection>
      <RACListBoxLoadMoreItem onLoadMore={onLoadMore} isLoading={isLoading}>
        <Spinner aria-label={collectionMessages.format('loadingMore')} size="small" marginY={8} />
      </RACListBoxLoadMoreItem>
    </RACListBox>
  );
}

export interface ComboBoxItemProps<T extends CollectionItem> extends Pick<
  RACListBoxItemProps<T>,
  'isDisabled' | 'onHoverStart' | 'onHoverEnd'
> {
  /** The ID of the item. Has to be unique across all sections and items. */
  id?: Key;
  /** The content to display as the label. */
  children: StringLikeChildren;
}

/** Renders a single list item within `ComboBox`. */
export function ComboBoxItem<T extends CollectionItem>({ children, ...props }: ComboBoxItemProps<T>) {
  const textValue =
    typeof children === 'string' ? children : typeof children === 'number' ? String(children) : children.join('');

  return (
    <RACListBoxItem {...props} className="cim-combo-box-item" textValue={textValue}>
      {({ isDisabled }) => (
        <>
          <IconCheckBold />

          <RACText slot="label" className={textStyle({ variant: 'body', tone: isDisabled ? 'muted' : 'base' })}>
            {children}
          </RACText>
        </>
      )}
    </RACListBoxItem>
  );
}

ComboBoxItem.displayName = 'ComboBoxItem';

export interface ComboBoxSectionProps<T extends CollectionItem> extends CollectionProps<T> {
  /** The ID of the section. Has to be unique across all sections and items. */
  id?: Key;
  /** The content to display as the section title. */
  title: string;
}

/** Groups list items within `ComboBox` into a section. */
export function ComboBoxSection<T extends CollectionItem>({
  title,
  children,
  items,
  ...props
}: ComboBoxSectionProps<T>) {
  if (!isProduction() && items != null && typeof children !== 'function') {
    console.warn('`ComboBoxSection` requires `children` to be a function when using `items` prop');
  }

  return (
    <RACListBoxSection {...props} className="cim-combo-box-section">
      <RACHeader
        className={clsx('cim-combo-box-section-header', textStyle({ variant: 'small-semibold', tone: 'subtle' }))}
      >
        {title}
      </RACHeader>
      <RACCollection items={items}>{children}</RACCollection>
    </RACListBoxSection>
  );
}

ComboBoxSection.displayName = 'ComboBoxSection';
